import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Crown, Send, CheckCircle, XCircle, Loader2, UserPlus, Trash2, ChevronDown, ChevronUp, Search, Check, Plus, Calendar, TrendingUp, Users, AlertTriangle, RefreshCw, Pencil, KeyRound } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return d; }
};

const PLAN_OPTIONS = [
  { value: 'founder_2y', label: 'Fondateur (2 ans)' },
  { value: 'annual', label: 'Annuel' },
  { value: 'monthly', label: 'Mensuel' },
];

const ClubMembersPanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);
  const [showMembers, setShowMembers] = useState(false);
  const [showFinancial, setShowFinancial] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [searchFilter, setSearchFilter] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  const [checkingExpiry, setCheckingExpiry] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', email: '', plan_type: 'monthly', start_date: '', end_date: '', price_ht: '', price_ttc: '' });
  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', plan_type: '', start_date: '', end_date: '', price_ht: '', price_ttc: '' });

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/club-members/status`, { withCredentials: true });
      setStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch club status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const members = status?.members || [];
  const filteredMembers = members.filter(m =>
    m.name?.toLowerCase().includes(searchFilter.toLowerCase()) ||
    m.email?.toLowerCase().includes(searchFilter.toLowerCase())
  );

  const toggleEmail = (email) => {
    setSelectedEmails(prev => {
      const next = new Set(prev);
      next.has(email) ? next.delete(email) : next.add(email);
      return next;
    });
  };

  const selectAll = () => {
    selectedEmails.size === filteredMembers.length
      ? setSelectedEmails(new Set())
      : setSelectedEmails(new Set(filteredMembers.map(m => m.email)));
  };

  const addMember = async (e) => {
    e.preventDefault();
    if (!newMember.name || !newMember.email || !newMember.start_date || !newMember.end_date) return;
    setAddingMember(true);
    setMessage(null);
    try {
      const payload = { ...newMember };
      if (payload.price_ht) payload.price_ht = parseFloat(payload.price_ht);
      else delete payload.price_ht;
      if (payload.price_ttc) payload.price_ttc = parseFloat(payload.price_ttc);
      else delete payload.price_ttc;
      const res = await axios.post(`${API}/api/club-members/add`, payload, { withCredentials: true });
      setMessage({ type: res.data.success ? 'success' : 'error', text: res.data.message });
      if (res.data.success) {
        setNewMember({ name: '', email: '', plan_type: 'monthly', start_date: '', end_date: '', price_ht: '', price_ttc: '' });
        setShowAddForm(false);
        fetchStatus();
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Erreur' });
    } finally {
      setAddingMember(false);
    }
  };

  const deleteMember = async (email, name) => {
    if (!window.confirm(`Supprimer ${name} du Club et passer en free ?`)) return;
    try {
      const res = await axios.post(`${API}/api/club-members/delete`, { email }, { withCredentials: true });
      setMessage({ type: res.data.success ? 'success' : 'error', text: res.data.message });
      fetchStatus();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Erreur' });
    }
  };

  const resetPassword = async (email, name) => {
    if (!window.confirm(`Réinitialiser le mot de passe de ${name} (${email}) à 10minvestor@ ?`)) return;
    try {
      const res = await axios.post(`${API}/api/club-members/reset-password`, { email }, { withCredentials: true });
      setMessage({ type: res.data.success ? 'success' : 'error', text: res.data.message });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Erreur' });
    }
  };

  const startEditMember = (m) => {
    setEditingMember(m.email);
    setEditForm({ name: m.name, email: m.email, plan_type: m.plan_type, start_date: m.start_date, end_date: m.end_date, price_ht: m.price_ht || '', price_ttc: m.price_ttc || '' });
  };

  const saveEditMember = async (oldEmail) => {
    try {
      const payload = { old_email: oldEmail, ...editForm };
      if (payload.price_ht) payload.price_ht = parseFloat(payload.price_ht);
      else delete payload.price_ht;
      if (payload.price_ttc) payload.price_ttc = parseFloat(payload.price_ttc);
      else delete payload.price_ttc;
      const res = await axios.post(`${API}/api/club-members/edit`, payload, { withCredentials: true });
      setMessage({ type: res.data.success ? 'success' : 'error', text: res.data.message });
      setEditingMember(null);
      fetchStatus();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Erreur' });
    }
  };

  const sendEmails = async () => {
    const count = selectedEmails.size > 0 ? selectedEmails.size : members.length;
    if (!window.confirm(`Envoyer le mail de bienvenue Club à ${count} membre(s) ?`)) return;
    setSending(true);
    setMessage(null);
    try {
      const emails = selectedEmails.size > 0 ? Array.from(selectedEmails) : [];
      const res = await axios.post(`${API}/api/club-members/send-emails`, { emails }, { withCredentials: true });
      setMessage({ type: 'success', text: res.data.message });
      fetchStatus();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Erreur' });
    } finally {
      setSending(false);
    }
  };

  const checkExpirations = async () => {
    setCheckingExpiry(true);
    try {
      const res = await axios.post(`${API}/api/club-members/check-expirations`, {}, { withCredentials: true });
      setMessage({ type: 'success', text: `${res.data.expired} expiré(s), ${res.data.downgraded} passé(s) en free` });
      fetchStatus();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Erreur' });
    } finally {
      setCheckingExpiry(false);
    }
  };

  const syncAccess = async () => {
    setSyncing(true);
    setMessage(null);
    try {
      const res = await axios.post(`${API}/api/club-members/sync-access`, {}, { withCredentials: true });
      setMessage({ type: 'success', text: res.data.message });
      fetchStatus();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Erreur de synchronisation' });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return (
    <Card className="border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 mb-8">
      <CardContent className="p-6 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-emerald-500 mr-2" /><span className="text-slate-600">Chargement...</span>
      </CardContent>
    </Card>
  );

  const fin = status?.financial || {};
  const emailStats = status?.email_stats || {};
  const renewals = status?.renewals_soon || [];
  const now = new Date().toISOString().slice(0, 10);

  return (
    <Card className="border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 mb-8" data-testid="club-members-panel">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Crown className="h-7 w-7 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">Membres Club</h3>
              <p className="text-slate-600 text-sm">
                {status?.active_members || 0} actifs | {status?.expired_members || 0} expirés | {members.length} total
                {selectedEmails.size > 0 && <span className="text-emerald-600 font-semibold"> | {selectedEmails.size} sélectionnés</span>}
              </p>
            </div>
          </div>
        </div>

        {/* Renewals Alert */}
        {renewals.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-700">
              <strong>{renewals.length} renouvellement(s)</strong> dans les 30 prochains jours :
              {renewals.slice(0, 3).map((r, i) => <span key={i} className="block text-xs">{r.name} — expire le {fmtDate(r.end_date)}</span>)}
            </div>
          </div>
        )}

        {/* Email stats */}
        {emailStats.total > 0 && (
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-white rounded-lg p-3 text-center border border-green-200">
              <div className="flex items-center justify-center gap-1 text-green-600 mb-1"><CheckCircle className="h-4 w-4" /><span className="text-lg font-bold">{emailStats.sent}</span></div>
              <p className="text-xs text-slate-500">Envoyés</p>
            </div>
            <div className="bg-white rounded-lg p-3 text-center border border-red-200">
              <div className="flex items-center justify-center gap-1 text-red-600 mb-1"><XCircle className="h-4 w-4" /><span className="text-lg font-bold">{emailStats.failed}</span></div>
              <p className="text-xs text-slate-500">Echoués</p>
            </div>
            <div className="bg-white rounded-lg p-3 text-center border border-amber-200">
              <div className="flex items-center justify-center gap-1 text-amber-600 mb-1"><Loader2 className="h-4 w-4" /><span className="text-lg font-bold">{emailStats.pending}</span></div>
              <p className="text-xs text-slate-500">En attente</p>
            </div>
          </div>
        )}

        {/* Message */}
        {message && (
          <div className={`rounded-lg p-3 mb-4 text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {message.text}
          </div>
        )}

        {/* Financial Dashboard Toggle */}
        <Button variant="outline" onClick={() => setShowFinancial(!showFinancial)} className="w-full mb-3 justify-between border-emerald-300 text-emerald-700" data-testid="toggle-financial">
          <span className="flex items-center gap-2"><TrendingUp className="h-4 w-4" /> Dashboard Financier</span>
          {showFinancial ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>

        {showFinancial && (
          <div className="mb-4 bg-white rounded-lg border border-slate-200 p-4">
            {/* Revenue summary */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-4 text-white">
                <p className="text-xs text-emerald-100 uppercase tracking-wide">Revenus HT (actifs)</p>
                <p className="text-2xl font-bold mt-1">{(fin.total_ht || 0).toLocaleString('fr-FR')} €</p>
              </div>
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-4 text-white">
                <p className="text-xs text-blue-100 uppercase tracking-wide">Revenus TTC (actifs)</p>
                <p className="text-2xl font-bold mt-1">{(fin.total_ttc || 0).toLocaleString('fr-FR')} €</p>
              </div>
            </div>

            {/* By plan */}
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Répartition par formule</h4>
            <div className="space-y-2 mb-4">
              {Object.entries(fin.by_plan || {}).map(([key, plan]) => {
                const maxCount = Math.max(...Object.values(fin.by_plan || {}).map(p => p.count), 1);
                return (
                  <div key={key} className="flex items-center gap-3">
                    <div className="w-32 text-xs text-slate-600 truncate">{plan.label}</div>
                    <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                      <div
                        className={`h-full rounded-full flex items-center pl-2 text-xs text-white font-bold ${
                          key === 'founder_2y' ? 'bg-emerald-500' : key === 'annual' ? 'bg-blue-500' : 'bg-amber-500'
                        }`}
                        style={{ width: `${Math.max((plan.count / maxCount) * 100, 15)}%` }}
                      >
                        {plan.count}
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 w-20 text-right">{plan.total_ht.toLocaleString('fr-FR')} € HT</div>
                  </div>
                );
              })}
            </div>

            {/* Members table */}
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Détail des membres</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 text-slate-500 font-medium">Nom</th>
                    <th className="text-left py-2 text-slate-500 font-medium">Formule</th>
                    <th className="text-left py-2 text-slate-500 font-medium">Début</th>
                    <th className="text-left py-2 text-slate-500 font-medium">Fin</th>
                    <th className="text-left py-2 text-slate-500 font-medium">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m, i) => {
                    const isExpired = m.end_date < now;
                    return (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="py-2 font-medium text-slate-900">{m.name}</td>
                        <td className="py-2 text-slate-600">{m.plan_label}</td>
                        <td className="py-2 text-slate-600">{fmtDate(m.start_date)}</td>
                        <td className="py-2 text-slate-600">{fmtDate(m.end_date)}</td>
                        <td className="py-2">
                          {isExpired
                            ? <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Expiré</span>
                            : <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Actif</span>
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Add member */}
        <div className="flex flex-wrap gap-2 mb-3">
          <Button variant="outline" onClick={() => setShowAddForm(!showAddForm)} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="add-club-member-toggle">
            <Plus className="h-4 w-4 mr-2" /> Ajouter un membre
          </Button>
          <Button variant="outline" onClick={checkExpirations} disabled={checkingExpiry} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="check-expiry-btn">
            {checkingExpiry ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Vérifier expirations
          </Button>
          <Button onClick={syncAccess} disabled={syncing} className="bg-[#0D47A1] hover:bg-[#0A367A] text-white" data-testid="sync-access-btn">
            {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Synchroniser les accès
          </Button>
        </div>

        {showAddForm && (
          <form onSubmit={addMember} className="mb-4 bg-white rounded-lg border border-slate-200 p-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input type="text" placeholder="Nom complet *" value={newMember.name} onChange={e => setNewMember(p => ({ ...p, name: e.target.value }))} required className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-400" />
              <input type="email" placeholder="Email *" value={newMember.email} onChange={e => setNewMember(p => ({ ...p, email: e.target.value }))} required className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-400" />
              <select value={newMember.plan_type} onChange={e => setNewMember(p => ({ ...p, plan_type: e.target.value }))} className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-400">
                {PLAN_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <div className="flex gap-2">
                <input type="date" placeholder="Date début" value={newMember.start_date} onChange={e => setNewMember(p => ({ ...p, start_date: e.target.value }))} required className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-400" />
                <input type="date" placeholder="Date fin" value={newMember.end_date} onChange={e => setNewMember(p => ({ ...p, end_date: e.target.value }))} required className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-400" />
              </div>
              <input type="number" step="0.01" placeholder="Prix HT (€)" value={newMember.price_ht} onChange={e => setNewMember(p => ({ ...p, price_ht: e.target.value }))} className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-400" />
              <input type="number" step="0.01" placeholder="Prix TTC (€)" value={newMember.price_ttc} onChange={e => setNewMember(p => ({ ...p, price_ttc: e.target.value }))} className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-400" />
            </div>
            <Button type="submit" disabled={addingMember} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {addingMember ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
              Ajouter + créer le compte Club
            </Button>
          </form>
        )}

        {/* Members list toggle */}
        <Button variant="outline" onClick={() => setShowMembers(!showMembers)} className="w-full mb-3 justify-between border-slate-300" data-testid="toggle-club-members">
          <span>Liste des membres ({members.length})</span>
          {showMembers ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>

        {showMembers && (
          <div className="mb-4 bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="p-3 border-b border-slate-200 flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input type="text" placeholder="Rechercher..." value={searchFilter} onChange={e => setSearchFilter(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-400" />
              </div>
              <Button variant="outline" size="sm" onClick={selectAll}>
                {selectedEmails.size === filteredMembers.length ? 'Désélectionner' : 'Tout sélectionner'}
              </Button>
            </div>
            <div className="max-h-72 overflow-y-auto">
              {filteredMembers.map((m, i) => {
                const isExpired = m.end_date < now;
                if (editingMember === m.email) {
                  return (
                    <div key={m.email} className="flex flex-wrap items-center gap-2 px-3 py-2 bg-emerald-50 border-b border-slate-100">
                      <input type="text" value={editForm.name} onChange={e => setEditForm(p => ({...p, name: e.target.value}))} className="flex-1 min-w-[120px] px-2 py-1 text-sm border rounded" placeholder="Nom" />
                      <input type="email" value={editForm.email} onChange={e => setEditForm(p => ({...p, email: e.target.value}))} className="flex-1 min-w-[150px] px-2 py-1 text-sm border rounded" placeholder="Email" />
                      <select value={editForm.plan_type} onChange={e => setEditForm(p => ({...p, plan_type: e.target.value}))} className="px-2 py-1 text-sm border rounded">
                        {PLAN_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <input type="date" value={editForm.start_date} onChange={e => setEditForm(p => ({...p, start_date: e.target.value}))} className="px-2 py-1 text-sm border rounded" />
                      <input type="date" value={editForm.end_date} onChange={e => setEditForm(p => ({...p, end_date: e.target.value}))} className="px-2 py-1 text-sm border rounded" />
                      <input type="number" step="0.01" value={editForm.price_ht} onChange={e => setEditForm(p => ({...p, price_ht: e.target.value}))} className="w-20 px-2 py-1 text-sm border rounded" placeholder="HT €" />
                      <input type="number" step="0.01" value={editForm.price_ttc} onChange={e => setEditForm(p => ({...p, price_ttc: e.target.value}))} className="w-20 px-2 py-1 text-sm border rounded" placeholder="TTC €" />
                      <Button size="sm" className="bg-green-600 text-white h-7 px-2 text-xs" onClick={() => saveEditMember(m.email)}>OK</Button>
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setEditingMember(null)}>X</Button>
                    </div>
                  );
                }
                return (
                  <label key={m.email} className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 border-b border-slate-100 last:border-0 ${selectedEmails.has(m.email) ? 'bg-emerald-50' : ''}`}
                    onClick={(e) => { e.preventDefault(); toggleEmail(m.email); }}>
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${selectedEmails.has(m.email) ? 'bg-emerald-600 border-emerald-600' : 'border-slate-300'}`}>
                      {selectedEmails.has(m.email) && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <input type="checkbox" className="sr-only" checked={selectedEmails.has(m.email)} onChange={() => toggleEmail(m.email)} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{m.name}</p>
                      <p className="text-xs text-slate-500 truncate">{m.email}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
                        m.plan_type === 'founder_2y' ? 'bg-emerald-100 text-emerald-700' :
                        m.plan_type === 'annual' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                      }`}>{m.plan_label?.replace('Membre ', '').replace('Abonnement ', '') || m.plan_type}</span>
                      <span className="text-xs text-slate-400">{fmtDate(m.end_date)}</span>
                      {isExpired && <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Expiré</span>}
                      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); startEditMember(m); }} className="p-1 text-slate-300 hover:text-blue-500 transition-colors" title="Modifier">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); resetPassword(m.email, m.name); }} className="p-1 text-slate-300 hover:text-amber-500 transition-colors" title="Réinitialiser mot de passe">
                        <KeyRound className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteMember(m.email, m.name); }} className="p-1 text-slate-300 hover:text-red-500 transition-colors" title="Supprimer">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button onClick={sendEmails} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700 text-white justify-start" data-testid="send-club-emails-btn">
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Envoyer mail Club {selectedEmails.size > 0 ? `(${selectedEmails.size})` : '(tous)'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ClubMembersPanel;
